import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { PaymentMethodProvider } from '__generated__/globalTypes';
import LoadingButton from 'atoms/buttons/LoadingButton';
import { SBHorizontal, Vertical } from 'atoms/layout/flex';
import CreditCard from 'components/buyActions/CreditCard';
import Warning from 'contexts/intl/Warning';

import SettingsSection from '../SettingsSection';
import { useCurrentUserPaymentMethods } from './useCurrentUserPaymentMethods';
import useDetachPaymentMethod from './useDetachPaymentMethod';

const messages = defineMessages({
  title: {
    id: 'Settings.paymentMethods.title',
    defaultMessage: 'Credit Cards',
  },
  empty: {
    id: 'Settings.paymentMethods.empty',
    defaultMessage: 'No credit card saved.',
  },
  remove: {
    id: 'Settings.paymentMethods.remove',
    defaultMessage: 'Remove',
  },
  disabled: {
    id: 'Settings.paymentMethods.disabled',
    defaultMessage: 'disabled',
  },
});

const Card = styled(SBHorizontal).attrs({ gap: 0 })`
  background-color: var(--c-neutral-300);
  border-radius: 8px;
  padding: var(--double-unit);
  &:first-child {
    margin-top: 0;
  }
`;

const DetachPaymentMethodButton = ({
  id,
  provider,
}: {
  id: string;
  provider: PaymentMethodProvider;
}) => {
  const { detachPaymentMethod, loading } = useDetachPaymentMethod();
  return (
    <LoadingButton
      loading={loading}
      color="quaternary"
      size="small"
      onClick={() => {
        detachPaymentMethod(id, provider);
      }}
    >
      <FormattedMessage {...messages.remove} />
    </LoadingButton>
  );
};

export const PaymentMethods = () => {
  const paymentMethods = useCurrentUserPaymentMethods();
  return (
    <SettingsSection title={messages.title}>
      {paymentMethods.length === 0 && (
        <Warning variant="grey" title={messages.empty} />
      )}
      {paymentMethods?.length > 0 && (
        <Vertical gap={2}>
          {paymentMethods.map(paymentMethod => (
            <Card key={paymentMethod.id}>
              <CreditCard
                creditCard={paymentMethod.card}
                color={
                  paymentMethod.disabled ? 'var(--c-neutral-600)' : undefined
                }
                suffixText={
                  paymentMethod.disabled ? (
                    <>
                      (<FormattedMessage {...messages.disabled} />)
                    </>
                  ) : undefined
                }
              />
              <DetachPaymentMethodButton
                id={paymentMethod.id}
                provider={paymentMethod.provider}
              />
            </Card>
          ))}
        </Vertical>
      )}
    </SettingsSection>
  );
};

export default PaymentMethods;
